import { Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageAttachment } from '../../../models/message.model';
import { saveBlobAsFile } from '../../../helpers/file-download';

export interface AttachmentPreviewDialogData {
  /** The image/PDF attachments from the source message eligible for
   *  preview — only ever images or a single PDF, filtered by the caller.
   *  Multiple entries enable next/prev navigation between them. */
  attachments: MessageAttachment[];
  /** Index into `attachments` of the one that was actually clicked. */
  index: number;
}

/**
 * Full-size preview for an image or PDF attachment. Everything else (Word
 * docs, Excel, generic files) opens/downloads directly and never routes here.
 */
@Component({
  selector: 'app-attachment-preview-dialog',
  imports: [MatDialogModule, MatIconModule, MatButtonModule],
  templateUrl: './attachment-preview-dialog.html',
  styleUrl: './attachment-preview-dialog.css',
})
export class AttachmentPreviewDialog {
  private dialogRef = inject(MatDialogRef<AttachmentPreviewDialog>);
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private data: AttachmentPreviewDialogData = inject(MAT_DIALOG_DATA);

  currentIndex = this.data.index ?? 0;

  get attachments(): MessageAttachment[] {
    return this.data.attachments;
  }

  get current(): MessageAttachment {
    return this.attachments[this.currentIndex];
  }

  get isImage(): boolean {
    return this.current.mime_type.startsWith('image/');
  }

  get isPdf(): boolean {
    return this.current.mime_type === 'application/pdf';
  }

  get safePdfUrl(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.current.url);
  }

  get hasMultiple(): boolean {
    return this.attachments.length > 1;
  }

  next(): void {
    this.currentIndex = (this.currentIndex + 1) % this.attachments.length;
  }

  prev(): void {
    this.currentIndex = (this.currentIndex - 1 + this.attachments.length) % this.attachments.length;
  }

  close(): void {
    this.dialogRef.close();
  }

  /**
   * Fetches the current attachment's bytes and saves them locally. Not a
   * plain `<a download>` — attachment.url is an authenticated proxy that
   * 302-redirects to a presigned S3 URL in production (see
   * server/routes/files.js), and the `download` attribute is ignored by
   * browsers across a cross-origin redirect. Fetching as a blob ourselves
   * sidesteps that entirely, since HttpClient follows the redirect and we
   * trigger the save from the resulting same-origin blob: URL instead.
   */
  download(): void {
    const attachment = this.current;
    this.http.get(attachment.url, { responseType: 'blob' }).subscribe({
      next: (blob) => saveBlobAsFile(blob, attachment.file_name),
      error: () => this.snackBar.open('Failed to download file.', 'Dismiss', { duration: 5000 }),
    });
  }
}
